import { Card, IconTile } from "@peer-start/ui";
import { TrendingUp } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { activeUsersAt } from "../data/manager-insights.js";
import { monthTicks, trendPoints, weekdayKeys, type TrendRange } from "../data/manager-trend.js";

const width = 1000;
const height = 200;
const floor = 50;
const ceiling = 95;
const gridLines = [0, 0.2, 0.4, 0.6, 0.8, 1];
const ranges = ["week", "month"] as const satisfies readonly TrendRange[];

const yFor = (value: number) => height - ((value - floor) / (ceiling - floor)) * height;

function smoothPath(points: [number, number][]): string {
  return points
    .map(([x, y], index) => {
      if (index === 0) return `M ${x},${y}`;
      const [x0, y0] = points[index - 2] ?? points[index - 1]!;
      const [x1, y1] = points[index - 1]!;
      const [x3, y3] = points[index + 1] ?? [x, y];
      return `C ${x1 + (x - x0) / 6},${y1 + (y - y0) / 6} ${x - (x3 - x1) / 6},${y - (y3 - y1) / 6} ${x},${y}`;
    })
    .join(" ");
}

export function ManagerActiveTrend() {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState<TrendRange>("week");
  const [active, setActive] = useState<number | null>(null);
  const gradientId = useId();
  const tipId = useId();
  const points = trendPoints(range);
  const step = width / Math.max(1, points.length - 1);
  const line = smoothPath(points.map((point, index): [number, number] => [index * step, yFor(point.share)]));
  const area = `${line} L ${width},${height} L 0,${height} Z`;
  const date = new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", timeZone: "UTC" });
  const moment = new Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
  const percent = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
  const hovered = active === null ? null : points[active];
  const users = hovered ? activeUsersAt(hovered.share) : null;
  const ticks =
    range === "week"
      ? weekdayKeys.map((key) => t(`managerDashboard.trend.days.${key}`))
      : monthTicks().map((tick) => date.format(tick));

  return (
    <Card className="mt-4" data-control-id="manager.dashboard.trend" padding="compact">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <IconTile>
            <TrendingUp className="size-5" />
          </IconTile>
          <h2 className="text-base font-medium">{t("managerDashboard.trend.title")}</h2>
        </div>
        <div
          aria-label={t("managerDashboard.trend.range")}
          className="flex rounded-full bg-muted p-1"
          role="group"
        >
          {ranges.map((option) => (
            <button
              aria-pressed={range === option}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30 ${range === option ? "bg-foreground text-background" : "text-foreground/80 hover:text-foreground"}`}
              data-control-id={`manager.dashboard.trend.${option}`}
              key={option}
              onClick={() => {
                setRange(option);
                setActive(null);
              }}
              type="button"
            >
              {t(`managerDashboard.trend.${option}`)}
            </button>
          ))}
        </div>
      </div>
      <p className="sr-only">
        {t("managerDashboard.trend.summary", {
          from: Math.round(points[0]?.share ?? 0),
          to: Math.round(points.at(-1)?.share ?? 0),
        })}
      </p>
      <div
        aria-describedby={hovered ? tipId : undefined}
        aria-label={t("managerDashboard.trend.title")}
        className="relative mt-4 h-48 rounded outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
        data-control-id="manager.dashboard.trend.plot"
        onBlur={() => setActive(null)}
        onFocus={() => setActive((current) => current ?? points.length - 1)}
        onKeyDown={(event) => {
          const direction =
            event.key === "ArrowRight" || event.key === "ArrowUp"
              ? 1
              : event.key === "ArrowLeft" || event.key === "ArrowDown"
                ? -1
                : 0;
          if (!direction) return;
          event.preventDefault();
          setActive((current) => Math.min(points.length - 1, Math.max(0, (current ?? 0) + direction)));
        }}
        onMouseLeave={() => setActive(null)}
        onMouseMove={(event) => {
          const bounds = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientX - bounds.left) / bounds.width;
          setActive(Math.min(points.length - 1, Math.max(0, Math.round(ratio * (points.length - 1)))));
        }}
        aria-orientation="horizontal"
        aria-valuemax={points.length - 1}
        aria-valuemin={0}
        aria-valuenow={active ?? points.length - 1}
        aria-valuetext={
          hovered
            ? `${moment.format(hovered.at)}, ${t("managerDashboard.usage.percent", { value: percent.format(hovered.share) })}`
            : undefined
        }
        role="slider"
        tabIndex={0}
      >
        <svg
          aria-hidden="true"
          className="h-full w-full overflow-visible"
          preserveAspectRatio="none"
          viewBox={`0 0 ${width} ${height}`}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--chart-3)" stopOpacity={0.16} />
              <stop offset="100%" stopColor="var(--chart-3)" stopOpacity={0} />
            </linearGradient>
          </defs>
          {gridLines.map((line) => (
            <line
              key={line}
              stroke="var(--border)"
              strokeOpacity={0.6}
              vectorEffect="non-scaling-stroke"
              x1={0}
              x2={width}
              y1={line * height}
              y2={line * height}
            />
          ))}
          <path d={area} fill={`url(#${gradientId})`} />
          <path
            d={line}
            fill="none"
            stroke="var(--info-foreground)"
            strokeLinejoin="round"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {hovered && active !== null ? (
          <TrendHover
            countLabel={users === null ? null : t("managerDashboard.trend.activeCount", { count: users })}
            id={tipId}
            momentLabel={moment.format(hovered.at)}
            percentLabel={t("managerDashboard.usage.percent", { value: percent.format(hovered.share) })}
            x={(active / Math.max(1, points.length - 1)) * 100}
            y={(yFor(hovered.share) / height) * 100}
          />
        ) : null}
      </div>
      <div aria-hidden="true" className="mt-3 flex justify-between text-xs text-muted-foreground">
        {ticks.map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </div>
    </Card>
  );
}

function TrendHover({
  countLabel,
  id,
  momentLabel,
  percentLabel,
  x,
  y,
}: {
  countLabel: string | null;
  id: string;
  momentLabel: string;
  percentLabel: string;
  x: number;
  y: number;
}) {
  const shiftY = y > 36 ? "calc(-100% - 14px)" : "16px";
  const place =
    x > 68
      ? { right: `calc(${100 - x}% + 8px)`, transform: `translateY(${shiftY})` }
      : {
          left: `${x}%`,
          transform: `translate(${x < 24 ? "8px" : "-50%"}, ${shiftY})`,
        };
  return (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 w-px bg-info-foreground/30"
        style={{ left: `${x}%` }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-info-foreground"
        style={{ left: `${x}%`, top: `${y}%` }}
      />
      <span
        className="pointer-events-none absolute z-10 w-max rounded-xl border border-border bg-background px-3 py-2 shadow-md"
        id={id}
        role="tooltip"
        style={{ top: `${y}%`, ...place }}
      >
        <span className="block text-xs text-muted-foreground">{momentLabel}</span>
        <span className="mt-1 block text-sm font-medium tabular-nums">{percentLabel}</span>
        {countLabel ? <span className="block text-sm">{countLabel}</span> : null}
      </span>
    </>
  );
}
